/* AXON theme behaviour. Every page works without this file; this makes it quicker:
   the bag opens in place, options pick the right variant, the kit adds itself to the bag. */
(() => {
  const A = window.AXON || {};
  const root = document.documentElement;
  const css = getComputedStyle(root);
  const ms = (n, d = 0) => parseFloat(css.getPropertyValue(n)) || d;
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];

  /* ---------- Money ---------- */
  const formatMoney = (cents, format = A.moneyFormat || "${{amount}}") => {
    const n = (Number(cents) || 0) / 100;
    const fix = (v, d, t = ",", dec = ".") => {
      const [i, f] = v.toFixed(d).split(".");
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? dec + f : "");
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fix(n, 2),
      amount_no_decimals: fix(n, 0),
      amount_with_comma_separator: fix(n, 2, ".", ","),
      amount_no_decimals_with_comma_separator: fix(n, 0, ".", ","),
      amount_with_apostrophe_separator: fix(n, 2, "'", "."),
    }[k] ?? fix(n, 2)));
  };
  const strip = (html) => { const d = document.createElement("div"); d.innerHTML = html; return d.textContent; };

  /* ---------- Dropdowns (transitions.dev 05): menu and search ---------- */
  // One panel, any number of toggles (the header's on wide screens, the tab bar's on phones).
  function dropdown(toggles, panel, { onOpen } = {}) {
    toggles = toggles.filter(Boolean);
    if (!toggles.length || !panel) return;
    const closeMs = ms("--dropdown-close-dur", 150);
    const isOpen = () => panel.classList.contains("is-open");
    let opener = toggles[0];
    const open = (from) => {
      closeAll(panel);
      opener = from || opener;
      panel.classList.remove("is-closing");
      panel.classList.add("is-open");
      toggles.forEach((t) => t.setAttribute("aria-expanded", "true"));
      onOpen && onOpen();
    };
    const close = ({ refocus = false } = {}) => {
      if (!isOpen()) return;
      panel.classList.remove("is-open");
      panel.classList.add("is-closing");
      toggles.forEach((t) => t.setAttribute("aria-expanded", "false"));
      setTimeout(() => panel.classList.remove("is-closing"), closeMs);
      if (refocus) opener.focus();
    };
    panel._close = close;
    toggles.forEach((t) => t.addEventListener("click", (e) => { e.preventDefault(); isOpen() ? close() : open(t); }));
    document.addEventListener("click", (e) => { if (!panel.contains(e.target) && !toggles.some((t) => t.contains(e.target))) close(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && isOpen()) close({ refocus: true }); });
    return { open, close };
  }
  function closeAll(except) { $$(".t-dropdown.is-open").forEach((p) => p !== except && p._close && p._close()); }

  function initHeader(scope = document) {
    const head = $("[data-header]", scope);
    if (!head || head._ready) return;
    head._ready = true;
    const sec = head.closest(".shopify-section") || document;
    const search = $("[data-search]", head);
    // Focus in the same tap, so phones raise the keyboard.
    const dd = dropdown($$("[data-search-toggle]", sec), search, { onOpen: () => $("#head-q")?.focus({ preventScroll: true }) });
    $("[data-search-close]", search)?.addEventListener("click", () => dd && dd.close({ refocus: true }));
    initMenu(sec);
  }

  /* ---------- Menu: React Bits StaggeredMenu, ported to vanilla + GSAP ----------
     Layers sweep in (70ms apart), then the panel, then each label rises from 140% with a
     10deg tilt, staggered 100ms. Close: everything slides out together in 320ms. */
  function initMenu(scope) {
    const toggles = $$("[data-sm-toggle]", scope);
    const menu = $("[data-sm]", scope);
    if (!toggles.length || !menu || menu._ready) return;
    menu._ready = true;
    const panel = $(".sm__panel", menu);
    const layers = $$(".sm__layer", menu);
    const labels = $$(".sm__label", menu);
    const more = $(".sm__more", menu);
    const setLabel = (t) => toggles.forEach((tg) => { const l = $("[data-sm-label]", tg); if (l) l.textContent = t; });
    const shown = (el) => el.offsetParent !== null || getComputedStyle(el).position === "fixed";
    const reduced = () => matchMedia("(prefers-reduced-motion: reduce)").matches;
    let open = false, tl = null, back = null;

    const show = () => {
      if (open) return;
      open = true;
      back = document.activeElement;
      root.classList.remove("sm-closing");
      root.classList.add("sm-open");
      document.body.classList.add("is-locked");
      toggles.forEach((t) => t.setAttribute("aria-expanded", "true"));
      setLabel("Close the menu");
      closeAll();
      const g = window.gsap;
      if (g && !reduced()) {
        tl && tl.kill();
        g.set([...layers, panel], { xPercent: 100 });
        g.set(labels, { yPercent: 140, rotate: 10 });
        g.set(more, { y: 24, autoAlpha: 0 });
        tl = g.timeline();
        layers.forEach((l, i) => tl.to(l, { xPercent: 0, duration: 0.5, ease: "power4.out" }, i * 0.07));
        const at = (layers.length - 1) * 0.07 + 0.08;
        tl.to(panel, { xPercent: 0, duration: 0.65, ease: "power4.out" }, at)
          .to(labels, { yPercent: 0, rotate: 0, duration: 1, ease: "power4.out", stagger: 0.1 }, at + 0.1)
          .to(more, { y: 0, autoAlpha: 1, duration: 0.55, ease: "power3.out" }, at + 0.26);
      }
      setTimeout(() => ($(".sm__link", menu) || panel).focus(), 60);
    };
    const hide = () => {
      if (!open) return;
      open = false;
      toggles.forEach((t) => t.setAttribute("aria-expanded", "false"));
      setLabel("Open the menu");
      document.body.classList.remove("is-locked");
      const done = () => { root.classList.remove("sm-open", "sm-closing"); };
      const g = window.gsap;
      if (g && !reduced()) {
        tl && tl.kill();
        root.classList.add("sm-closing");
        root.classList.remove("sm-open");
        tl = g.to([...layers, panel], { xPercent: 100, duration: 0.32, ease: "power3.in", overwrite: "auto", onComplete: done });
      } else done();
      back && back.focus && back.focus();
    };
    toggles.forEach((t) => t.addEventListener("click", (e) => { e.preventDefault(); open ? hide() : show(); }));
    menu.addEventListener("click", (e) => { if (e.target.closest("[data-sm-close]")) hide(); });
    document.addEventListener("keydown", (e) => {
      if (!open) return;
      if (e.key === "Escape") { hide(); return; }
      if (e.key === "Tab") {
        // Header toggle comes before the panel, the tab bar's after it: keep document order.
        const f = [...toggles.filter(shown), ...$$("a[href], button, input", panel).filter((el) => el.offsetParent !== null)]
          .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    window.AXON.closeMenu = hide;
  }

  /* ---------- Shop by collection: React Bits FlowingMenu, ported ----------
     The band slides in from whichever edge (top or bottom) the pointer crossed, its inner
     layer counter-slides so the marquee appears to stay still, and the marquee runs only
     while the row is lit. Keyboard focus lights the row from the top. */
  function initFlow(scope = document) {
    $$("[data-flow]", scope).forEach((list) => {
      if (list._ready) return;
      list._ready = true;
      const fine = matchMedia("(hover: hover) and (min-width: 960px)");
      $$(".flow__row", list).forEach((row) => {
        const band = $(".flow__band", row), inner = $(".flow__inner", row), link = $(".flow__link", row);
        const edge = (e) => {
          const r = row.getBoundingClientRect();
          return e && typeof e.clientY === "number" && e.clientY - r.top < r.height / 2 ? "top" : "bottom";
        };
        const go = (from, entering) => {
          const g = window.gsap;
          const off = from === "top" ? -101 : 101;
          if (entering) row.classList.add("is-flowing");
          if (!g || matchMedia("(prefers-reduced-motion: reduce)").matches) {
            band.style.transform = entering ? "translateY(0)" : `translateY(${off}%)`;
            inner.style.transform = entering ? "translateY(0)" : `translateY(${-off}%)`;
            if (!entering) row.classList.remove("is-flowing");
            return;
          }
          const d = { duration: 0.6, ease: "expo.out" };
          if (entering) {
            g.timeline({ defaults: d }).set(band, { yPercent: off, y: 0 }).set(inner, { yPercent: -off, y: 0 }).to([band, inner], { yPercent: 0 });
          } else {
            g.timeline({ defaults: d, onComplete: () => row.classList.remove("is-flowing") }).to(band, { yPercent: off }).to(inner, { yPercent: -off }, "<");
          }
        };
        row.addEventListener("pointerenter", (e) => { if (fine.matches) go(edge(e), true); });
        row.addEventListener("pointerleave", (e) => { if (fine.matches) go(edge(e), false); });
        link.addEventListener("focus", () => { if (fine.matches) go("top", true); });
        link.addEventListener("blur", () => { if (fine.matches) go("bottom", false); });
      });
    });
  }

  /* ---------- Bag ---------- */
  const drawer = () => $("[data-cart-drawer]");
  let lastFocus = null;
  const onCartPage = () => document.body.classList.contains("template-cart");

  function setCount(n) {
    $$("[data-cart-count]").forEach((el) => {
      el.dataset.count = n;
      el.innerHTML = `<span class="visually-hidden">, items: </span>${n}`;
    });
    A.cartCount = n;
  }
  function openCart() {
    const d = drawer();
    if (!d || onCartPage()) { window.location.href = A.routes.cart; return; }
    window.AXON.closeMenu && window.AXON.closeMenu();
    lastFocus = document.activeElement;
    root.classList.add("cart-open");
    document.body.classList.add("is-locked");
    setTimeout(() => d.focus(), 50);
  }
  function closeCart() {
    if (!root.classList.contains("cart-open")) return;
    root.classList.remove("cart-open");
    root.classList.add("cart-closing");
    setTimeout(() => root.classList.remove("cart-closing"), ms("--drawer-close", 220));
    document.body.classList.remove("is-locked");
    lastFocus && lastFocus.focus && lastFocus.focus();
  }
  function renderDrawer(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const fresh = $("[data-cart-inner]", doc);
    const inner = $("[data-cart-inner]");
    if (fresh && inner) inner.replaceWith(fresh);
  }
  async function refreshCart(sectionsHtml) {
    if (sectionsHtml && sectionsHtml["cart-drawer"]) renderDrawer(sectionsHtml["cart-drawer"]);
    else {
      const r = await fetch(`${A.routes.root}?sections=cart-drawer`);
      if (r.ok) renderDrawer((await r.json())["cart-drawer"]);
    }
    const cart = await (await fetch(`${A.routes.cart}.js`)).json();
    setCount(cart.item_count);
    A.cartTotal = cart.total_price;
    document.dispatchEvent(new CustomEvent("axon:cart", { detail: cart }));
    return cart;
  }
  async function addItems(items) {
    const r = await fetch(`${A.routes.cartAdd}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items, sections: "cart-drawer", sections_url: window.location.pathname }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message || "That couldn't be added.");
    await refreshCart(data.sections);
    return data;
  }
  async function changeLine(line, quantity) {
    root.classList.add("cart-busy");
    try {
      const r = await fetch(`${A.routes.cartChange}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line, quantity, sections: "cart-drawer", sections_url: window.location.pathname }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || "The bag couldn't be updated.");
      if (onCartPage()) { window.location.reload(); return; }
      await refreshCart(data.sections);
    } catch (err) {
      const msg = $("[data-cart-msg]");
      if (msg) msg.textContent = err.message;
    } finally {
      root.classList.remove("cart-busy");
    }
  }

  document.addEventListener("click", (e) => {
    const opener = e.target.closest("[data-cart-open]");
    if (opener) { e.preventDefault(); openCart(); return; }
    if (e.target.closest("[data-cart-close]")) { closeCart(); return; }
    const remove = e.target.closest("[data-line-remove]");
    if (remove) { changeLine(Number(remove.closest("[data-line]").dataset.line), 0); return; }
    const step = e.target.closest("[data-qty-step]");
    if (step) {
      const box = step.closest("[data-qty]");
      const input = $("[data-qty-input]", box);
      const min = Number(input.min || 0);
      input.value = Math.max(min, (Number(input.value) || 0) + Number(step.dataset.qtyStep));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });
  document.addEventListener("change", (e) => {
    const input = e.target.closest("[data-line] [data-qty-input]");
    if (input) changeLine(Number(input.closest("[data-line]").dataset.line), Math.max(0, Number(input.value) || 0));
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeCart();
    if (e.key === "Tab" && root.classList.contains("cart-open")) {
      const f = $$("a[href], button:not([disabled]), input, select, textarea", drawer()).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Accordions (transitions.dev 21) ---------- */
  document.addEventListener("click", (e) => {
    const head = e.target.closest(".t-acc-head");
    if (!head) return;
    const acc = head.closest(".t-acc");
    const open = acc.getAttribute("data-open") !== "true";
    acc.setAttribute("data-open", String(open));
    head.setAttribute("aria-expanded", String(open));
  });

  /* ---------- Product page ---------- */
  function initProduct(scope = document) {
    $$("[data-product]", scope).forEach((sec) => {
      if (sec._ready) return;
      sec._ready = true;
      const data = JSON.parse($("[data-product-json]", sec)?.textContent || "{}");
      const form = $("[data-product-form]", sec);
      if (!form || !data.variants) return;
      const select = $("[data-variant-select]", sec);
      const addBtn = $("[data-add]", sec);
      const priceEl = $("[data-price]", sec);
      const msg = $("[data-product-msg]", sec);
      const sets = $$("[data-option]", sec);

      const chosen = () => sets.map((fs) => ($("input:checked", fs) || {}).value);
      const findVariant = (opts) => data.variants.find((v) => v.options.every((o, i) => o === opts[i]));

      const markAvailability = () => {
        const cur = chosen();
        sets.forEach((fs, i) => {
          $$(".chip", fs).forEach((chip) => {
            const val = $("input", chip).value;
            const test = cur.slice(); test[i] = val;
            const v = findVariant(test);
            const ok = v && v.available;
            chip.classList.toggle("is-unavailable", !ok);
            $("span", chip).setAttribute("title", ok ? "" : v ? "Sold out" : "Not made in this combination");
          });
        });
      };

      const update = () => {
        const cur = chosen();
        sets.forEach((fs, i) => { const out = $("[data-option-value]", fs); if (out) out.textContent = cur[i] || ""; });
        const v = findVariant(cur);
        markAvailability();
        msg && (msg.textContent = "");
        if (!v) {
          addBtn.disabled = true; addBtn.textContent = "Unavailable";
          sec._syncBar && sec._syncBar(null);
          return;
        }
        select.value = String(v.id);
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? "Add to bag" : "Sold out";
        sec._syncBar && sec._syncBar(v);
        priceEl.innerHTML = v.compare > v.price
          ? `<s><span class="visually-hidden">Was </span>${formatMoney(v.compare)}</s><span class="is-sale"><span class="visually-hidden">Now </span>${formatMoney(v.price)}</span>`
          : `<span>${formatMoney(v.price)}</span>`;
        const url = new URL(window.location.href);
        url.searchParams.set("variant", v.id);
        history.replaceState(history.state, "", url);
        if (v.media) {
          const shot = $(`[data-media-id="${v.media}"]`, sec);
          const gal = $("[data-gallery]", sec);
          if (shot && gal) {
            if (gal.scrollWidth > gal.clientWidth + 4) gal.scrollTo({ left: shot.offsetLeft - gal.offsetLeft, behavior: "smooth" });
            else if (shot !== gal.firstElementChild) gal.prepend(shot);
          }
        }
      };
      sets.forEach((fs) => fs.addEventListener("change", update));
      markAvailability();
      initBuyBar(sec, addBtn, () => findVariant(chosen()));

      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        if (addBtn.disabled) return;
        addBtn.classList.add("is-busy");
        msg.textContent = "";
        try {
          const qty = Math.max(1, Number($("[data-qty-input]", form)?.value) || 1);
          await addItems([{ id: Number(select.value), quantity: qty }]);
          openCart();
        } catch (err) {
          msg.textContent = err.message;
          // Added from the buy bar: bring the message (and the options it is about) into view.
          if (root.classList.contains("has-buybar")) msg.scrollIntoView({ block: "center", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
        } finally {
          addBtn.classList.remove("is-busy");
        }
      });

      $("[data-open-guide]", sec)?.addEventListener("click", () => {
        const g = $("[data-guide]", sec);
        if (!g) return;
        g.setAttribute("data-open", "true");
        $(".t-acc-head", g).setAttribute("aria-expanded", "true");
        g.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
      });

      const gal = $("[data-gallery]", sec);
      const count = $("[data-gallery-count]", sec);
      if (gal && count) {
        const total = gal.children.length;
        gal.addEventListener("scroll", () => {
          const i = Math.round(gal.scrollLeft / (gal.firstElementChild.offsetWidth + 8));
          count.textContent = `${Math.min(total, i + 1)} / ${total}`;
        }, { passive: true });
      }
    });
  }

  /* ---------- Phones: the buy bar ----------
     Once the real Add to bag button has scrolled away above, a bar with the name, the chosen
     options and the price rises over the tab bar. It submits the same form, so it adds exactly
     what is picked above. */
  function initBuyBar(sec, addBtn, current) {
    const bar = $("[data-buybar]", sec);
    if (!bar || !addBtn || !("IntersectionObserver" in window)) return;
    const meta = $("[data-buybar-meta]", bar);
    const btn = $("[data-buybar-add]", bar);
    const phone = matchMedia("(max-width: 959px)");
    bar.hidden = false;
    bar.inert = true;
    sec._syncBar = (v) => {
      if (!v) { btn.disabled = true; btn.textContent = "Unavailable"; return; }
      btn.disabled = !v.available;
      btn.textContent = v.available ? "Add to bag" : "Sold out";
      const opts = v.options.filter((o) => o && o !== "Default Title").join(" / ");
      meta.textContent = formatMoney(v.price) + (opts ? ` \u00b7 ${opts}` : "");
    };
    sec._syncBar(current());
    let past = false;
    const set = () => {
      const on = past && phone.matches;
      bar.classList.toggle("is-shown", on);
      bar.inert = !on;
      root.classList.toggle("has-buybar", on);
    };
    new IntersectionObserver(([e]) => { past = !e.isIntersecting && e.boundingClientRect.top < 0; set(); }).observe(addBtn);
    phone.addEventListener("change", set);
  }

  /* ---------- Phones: swipe rails get a position count ---------- */
  function initRails(scope = document) {
    $$("[data-rail]", scope).forEach((rail) => {
      if (rail._ready) return;
      rail._ready = true;
      const sec = rail.closest("section") || document;
      const count = $(rail.dataset.rail, sec);
      const label = $("[data-reel-label]", sec);
      const items = [...rail.children];
      if (!count || items.length < 2) return;
      let shown = -1;
      const tick = () => {
        const mid = rail.getBoundingClientRect().left + rail.clientWidth / 2;
        let best = 0, d = Infinity;
        items.forEach((it, i) => { const r = it.getBoundingClientRect(); const x = Math.abs(r.left + r.width / 2 - mid); if (x < d) { d = x; best = i; } });
        if (best === shown) return;
        shown = best;
        count.textContent = `${best + 1} / ${items.length}`;
        const cap = $(".reel__cap", items[best]);
        if (label) label.innerHTML = cap ? cap.innerHTML : "";
      };
      rail.addEventListener("scroll", tick, { passive: true });
      tick();
    });
  }

  /* ---------- Recommendations ---------- */
  function initRecs(scope = document) {
    $$("[data-recs]", scope).forEach(async (box) => {
      if (box._ready || box.children.length) return;
      box._ready = true;
      try {
        const r = await fetch(box.dataset.url);
        if (!r.ok) return;
        const doc = new DOMParser().parseFromString(await r.text(), "text/html");
        const fresh = $("[data-recs]", doc);
        if (fresh && fresh.children.length) {
          box.innerHTML = fresh.innerHTML;
          document.dispatchEvent(new CustomEvent("axon:content", { detail: box }));
        }
      } catch {}
    });
  }

  /* ---------- Home: kit your day ---------- */
  function initKit(scope = document) {
    $$("[data-kit]", scope).forEach((kit) => {
      if (kit._ready) return;
      kit._ready = true;
      const picker = $("[data-picker]", kit);
      if (!picker) return;
      const data = JSON.parse($("[data-kit-data]", kit)?.textContent || "{}");
      const pieces = data.pieces || [];
      const list = $("[data-picker-list]", kit);
      const total = $("[data-picker-total]", kit);
      const add = $("[data-kit-add]", kit);
      const status = $("[data-kit-status]", kit);
      const free = A.freeShipping || 0;
      let cartTotal = data.cartTotal || 0;
      const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

      const current = () => {
        const s = ($('input[data-group="session"]:checked', picker) || {}).value;
        const a = ($('input[data-group="after"]:checked', picker) || {}).value;
        return pieces.filter((p) => p.when === s).concat(pieces.filter((p) => p.when === a));
      };
      const sumLine = (kitSum) => {
        let line = `<strong>${formatMoney(kitSum)}</strong> for the day.`;
        if (free > 0) {
          const gap = free - kitSum - cartTotal;
          line += gap > 0
            ? ` Add ${formatMoney(gap)}${cartTotal > 0 ? " more (with your bag)" : ""} for free shipping.`
            : cartTotal > 0 ? " With your bag, it ships free." : " Ships free.";
        }
        total.innerHTML = line;
      };
      const render = () => {
        const kitPieces = current();
        list.innerHTML = kitPieces.map((p, i) => {
          const avail = p.variants.filter((v) => v.available);
          const single = p.variants.length === 1;
          const opts = p.variants.map((v) => `<option value="${v.id}"${v.available ? "" : " disabled"}>${esc(v.title)}${v.available ? "" : " (sold out)"}</option>`).join("");
          return `<li class="piece">
            <a class="piece__link" href="${p.url}">${p.image ? `<img src="${p.image}" alt="" width="600" height="600" loading="lazy">` : ""}<span class="piece__name">${esc(p.title)}</span><span class="piece__price">${formatMoney(p.price)}</span></a>
            ${single ? `<input type="hidden" data-kit-variant value="${p.variants[0].id}">` : avail.length
              ? `<div class="select-wrap"><label class="visually-hidden" for="kit-${kit.id}-${i}">Size for ${esc(p.title)}</label><select class="select" id="kit-${kit.id}-${i}" data-kit-variant><option value="">Pick a size</option>${opts}</select></div>`
              : `<p class="piece__price">Sold out</p>`}
          </li>`;
        }).join("");
        sumLine(kitPieces.reduce((t, p) => t + p.price, 0));
        add.hidden = !kitPieces.some((p) => p.variants.some((v) => v.available));
        status.textContent = "";
      };

      $$(".t-tabs", picker).forEach((bar) => {
        const pill = $(".t-tabs-pill", bar);
        const tabs = $$(".t-tab", bar);
        const move = (animate) => {
          const i = Math.max(0, tabs.findIndex((t) => $("input", t).checked));
          if (!animate) { pill.style.transition = "none"; pill.style.transform = `translateX(${i * 100}%)`; void pill.offsetWidth; pill.style.transition = ""; }
          else pill.style.transform = `translateX(${i * 100}%)`;
        };
        bar.addEventListener("change", () => { move(true); render(); });
        move(false);
      });

      add.addEventListener("click", async () => {
        const fields = $$("[data-kit-variant]", list);
        const missing = fields.find((f) => !f.value);
        if (missing) { status.textContent = "Pick a size for each piece first."; missing.focus(); return; }
        const items = fields.map((f) => ({ id: Number(f.value), quantity: 1 }));
        add.classList.add("is-busy");
        status.textContent = "";
        try {
          const cart = await addItems(items);
          status.textContent = "The kit is in your bag.";
          openCart();
        } catch (err) {
          status.textContent = err.message;
        } finally {
          add.classList.remove("is-busy");
        }
      });
      list.addEventListener("change", () => { status.textContent = ""; });
      document.addEventListener("axon:cart", (e) => { cartTotal = e.detail.total_price; sumLine(current().reduce((t, p) => t + p.price, 0)); });
      render();
    });
  }

  /* ---------- Home: collection spotlight ---------- */
  function initShop(scope = document) {
    $$("[data-shop]", scope).forEach((shop) => {
      if (shop._ready) return;
      shop._ready = true;
      const items = $$(".item", shop);
      const shots = $$("[data-shop-frame] img", shop);
      if (!items.length) return;
      let current = 0;
      const activate = (i, { open = true } = {}) => {
        shots.forEach((s, k) => s.classList.toggle("is-active", k === i));
        if (!open) return;
        current = i;
        items.forEach((it, k) => {
          it.classList.toggle("is-active", k === i);
          $(".item__head", it).setAttribute("aria-expanded", String(k === i));
        });
      };
      const fine = matchMedia("(hover: hover) and (pointer: fine)");
      items.forEach((it, i) => {
        const head = $(".item__head", it);
        head.addEventListener("click", () => activate(i));
        head.addEventListener("focus", () => activate(i));
        it.addEventListener("pointerenter", () => { if (fine.matches) activate(i); });
      });
      let settle;
      const io = new IntersectionObserver((entries) => {
        const hit = entries.find((e) => e.isIntersecting);
        if (!hit) return;
        const i = items.indexOf(hit.target);
        clearTimeout(settle);
        settle = setTimeout(() => { if (i !== current || !fine.matches) activate(i, { open: fine.matches }); }, 120);
      }, { rootMargin: "-50% 0px -49% 0px" });
      items.forEach((it) => io.observe(it));
    });
  }

  /* ---------- Collection filters: apply as soon as something changes ---------- */
  function initFilters(scope = document) {
    $$("[data-filter-form]", scope).forEach((form) => {
      if (form._ready) return;
      form._ready = true;
      const submit = () => {
        const params = new URLSearchParams(new FormData(form));
        [...params.keys()].forEach((k) => { if (params.get(k) === "") params.delete(k); });
        window.location.href = `${form.action}?${params.toString()}`;
      };
      form.addEventListener("change", (e) => { if (e.target.matches('input[type="checkbox"], select')) submit(); });
      form.addEventListener("submit", (e) => { e.preventDefault(); submit(); });
      const details = $$("details", form);
      details.forEach((d) => d.addEventListener("toggle", () => { if (d.open) details.forEach((o) => o !== d && (o.open = false)); }));
      document.addEventListener("click", (e) => { if (!form.contains(e.target)) details.forEach((d) => (d.open = false)); });

      // Phones: the filters live in a sheet that rises from the bottom.
      const openBtn = $("[data-sheet-open]", form);
      const sheet = $("[data-sheet]", form);
      if (!openBtn || !sheet) return;
      let back = null;
      const show = () => {
        back = document.activeElement;
        root.classList.add("sheet-open");
        document.body.classList.add("is-locked");
        openBtn.setAttribute("aria-expanded", "true");
        if (details[0] && !details.some((d) => d.open)) details[0].open = true;
        setTimeout(() => $("[data-sheet-close]", sheet)?.focus(), 30);
      };
      const hide = () => {
        if (!root.classList.contains("sheet-open")) return;
        root.classList.remove("sheet-open");
        document.body.classList.remove("is-locked");
        openBtn.setAttribute("aria-expanded", "false");
        back && back.focus && back.focus();
      };
      openBtn.addEventListener("click", show);
      form.addEventListener("click", (e) => { if (e.target.closest("[data-sheet-close]") || e.target === $("[data-sheet-scrim]", form)) hide(); });
      document.addEventListener("keydown", (e) => {
        if (!root.classList.contains("sheet-open")) return;
        if (e.key === "Escape") hide();
        if (e.key === "Tab") {
          const f = $$("summary, a[href], button, input:not([disabled]), select", sheet).filter((el) => el.offsetParent !== null);
          const first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      matchMedia("(min-width: 960px)").addEventListener("change", (m) => m.matches && hide());
    });
  }

  function init(scope = document) {
    initHeader(scope);
    initFlow(scope);
    initProduct(scope);
    initRecs(scope);
    initKit(scope);
    initShop(scope);
    initFilters(scope);
    initRails(scope);
  }
  init();

  // Theme editor: sections are re-rendered in place.
  document.addEventListener("shopify:section:load", (e) => { init(e.target); document.dispatchEvent(new CustomEvent("axon:content", { detail: e.target })); });

  window.AXON.openCart = openCart;
  window.AXON.addItems = addItems;
  window.AXON.formatMoney = formatMoney;
})();
